import * as React from 'react';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';

import { cn, customImageLoader } from '@restworld/utils-common';
import { PhoneIcon, EnvelopeIcon } from '@heroicons/react/24/outline';
import { LinkBase } from '@restworld/ui-components';
import { Heading } from '@restworld/ui-ds-base';

export function ContactRestworldManager(props: ContactRestworldManagerProps) {
  const { t: translateCommon } = useTranslation('common');
  const { rwManager: { email, firstName, lastName, phone, image = '' } = {} } = props;

  return (
    <MainWrapper>
      <Heading size={5} weight="semibold" color="neutral" className="mb-3 w-full text-lg">
        {translateCommon('contact_your_restworld_manager')}
      </Heading>
      <Heading as="p" size={5} weight="medium" color="neutral-light" className="mb-8">
        {`${translateCommon(
          'to_change_venue_details_or_manage_positions_contact_your_manager_immediately_via_live_chat_phone_or_email'
        )} ${translateCommon('we_are_here_to_help_you')}`}
      </Heading>
      <InfoWrapper>
        {image && (
          <ImageWrapper>
            <Image src={image} alt="" fill loader={customImageLoader} />
          </ImageWrapper>
        )}
        {(firstName || lastName) && (
          <Heading size={5} weight="semibold" color="neutral">
            {`${translateCommon('the_contacts_of')} ${firstName || ''} ${lastName || ''}`}
          </Heading>
        )}
      </InfoWrapper>
      {phone && (
        <LinkBase newTab={false} link={`tel:${phone}`}>
          <div className="flex items-center gap-1 mx-auto w-fit mb-3">
            <PhoneIcon className="h-4 w-4 text-neutral-200" />
            <Heading as="span" size={5} weight="medium" color="neutral-light">
              {phone}
            </Heading>
          </div>
        </LinkBase>
      )}
      {email && (
        <LinkBase newTab={false} link={`mailto:${email}`}>
          <div className="flex items-center gap-1 mx-auto w-fit mb-8">
            <EnvelopeIcon className="h-4 w-4 text-neutral-200" />
            <Heading as="span" size={5} weight="medium" color="neutral-light">
              {email}
            </Heading>
          </div>
        </LinkBase>
      )}
    </MainWrapper>
  );
}
export default ContactRestworldManager;

export interface ContactRestworldManagerProps {
  rwManager?: { firstName: string; lastName: string; email: string; phone: string; image: string };
}

const MainWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('max-w-md mx-auto text-center', className)} {...props} />
);
const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('rounded-full overflow-hidden h-8 w-8 relative', className)} {...props} />
);
const InfoWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div className={cn('flex items-center justify-center gap-2 mb-3', className)} {...props} />
);
